import { ApolloLink, Observable } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { print } from 'graphql';

type Operation = ApolloLink.Operation;
type FetchResult<TData = Record<string, any>, TExtensions = Record<string, any>> = ApolloLink.Result<TData, TExtensions>;
type SseFailurePhase = 'connection' | 'stream' | 'timeout';

let requestSequence = 0;

const getErrorDetails = (error: unknown) => {
  if (error instanceof Error) {
    return { name: error.name, message: error.message };
  }

  if (typeof error === 'object' && error !== null) {
    const errorLike = error as { name?: unknown; message?: unknown };
    return {
      name: typeof errorLike.name === 'string' ? errorLike.name : 'UnknownError',
      message: typeof errorLike.message === 'string' ? errorLike.message : 'Unknown error'
    };
  }

  return { name: 'UnknownError', message: String(error) };
};

export class SSELink extends ApolloLink {
  private url: string;
  private streamTimeoutMs = 30000; // 30 second timeout for inactivity

  constructor(url: string) {
    super();
    this.url = url;
  }

  public request(operation: Operation): Observable<FetchResult> {
    return new Observable((observer) => {
      const query = print(operation.query);
      const variables = operation.variables;
      const controller = new AbortController();
      const requestId = `sse-${++requestSequence}`;
      const operationName = operation.operationName || 'anonymous';
      const startedAt = Date.now();
      const hiddenAtStart = document.hidden;
      let wasHiddenDuringRequest = hiddenAtStart;
      let visibilityChangeCount = 0;
      let responseStatus: number | null = null;
      let responseReceivedAt: number | null = null;
      let firstDataAt: number | null = null;
      let lastDataAt: number | null = null;
      let dataEventCount = 0;
      let bytesReceived = 0;
      let isClosed = false;

      const addBreadcrumb = (message: string, data: Record<string, unknown> = {}) => {
        Sentry.addBreadcrumb({
          category: 'sse',
          level: 'info',
          message,
          data: { requestId, operationName, ...data }
        });
      };

      const visibilityListener = () => {
        visibilityChangeCount += 1;
        wasHiddenDuringRequest = wasHiddenDuringRequest || document.hidden;
        addBreadcrumb('SSE visibility changed', {
          hidden: document.hidden,
          elapsedMs: Date.now() - startedAt
        });
      };

      const getFailureContext = (failurePhase: SseFailurePhase) => ({
        requestId,
        operationName,
        phase: failurePhase,
        elapsedMs: Date.now() - startedAt,
        hiddenAtStart,
        hiddenNow: document.hidden,
        wasHiddenDuringRequest,
        visibilityChangeCount,
        responseStatus,
        responseReceived: responseReceivedAt !== null,
        responseElapsedMs: responseReceivedAt === null ? null : responseReceivedAt - startedAt,
        firstDataElapsedMs: firstDataAt === null ? null : firstDataAt - startedAt,
        lastDataElapsedMs: lastDataAt === null ? null : lastDataAt - startedAt,
        dataEventCount,
        bytesReceived,
        cleanupRequested: isClosed,
        abortSignal: controller.signal.aborted
      });

      const captureFailure = (error: unknown, failurePhase: SseFailurePhase) => {
        const errorDetails = getErrorDetails(error);
        const failureContext = getFailureContext(failurePhase);

        Sentry.withScope((scope) => {
          scope.setTag('sse.operation', operationName);
          scope.setTag('sse.phase', failurePhase);
          scope.setContext('sse_transport', {
            ...failureContext,
            errorName: errorDetails.name,
            errorMessage: errorDetails.message
          });
          Sentry.captureException(error instanceof Error ? error : new Error(errorDetails.message));
        });
      };

      document.addEventListener('visibilitychange', visibilityListener);
      addBreadcrumb('SSE request started', {
        hidden: hiddenAtStart
      });

      const body = JSON.stringify({
        query: query,
        variables: variables
      });

      // Track timeout handle to detect stalled connections
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

      const setStreamTimeout = () => {
        if (timeoutHandle) clearTimeout(timeoutHandle);
        timeoutHandle = setTimeout(() => {
          const timeoutError = new Error('SSE stream timeout: No data received for 30+ seconds');
          captureFailure(timeoutError, 'timeout');
          console.debug('[SSELink] Stream timeout:', timeoutError.message, getFailureContext('timeout'));
          reader?.cancel().catch(() => {});
          observer.error(timeoutError);
        }, this.streamTimeoutMs);
      };

      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

      fetch(this.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        credentials: 'include',
        signal: controller.signal,
        body: body
      }).then(response => {
        responseStatus = response.status;
        responseReceivedAt = Date.now();
        addBreadcrumb('SSE response received', {
          status: response.status,
          elapsedMs: responseReceivedAt - startedAt
        });

        if (!response.ok) {
          throw new Error(`SSE HTTP Error ${response.status}: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error('SSE response body is empty');
        }

        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let lineBuffer = '';

        // Set initial timeout for first data
        setStreamTimeout();

        function processLine(line: string) {
          if (!line.startsWith('data: ')) return;
          try {
            // Reset timeout on each successful data reception
            setStreamTimeout();
            
            const rawData = JSON.parse(line.substring(6));
            const now = Date.now();
            firstDataAt ??= now;
            lastDataAt = now;
            dataEventCount += 1;
            observer.next(rawData);
          } catch (error) {
            console.error('[SSELink] Error parsing SSE data:', error, 'line:', line);
          }
        }

        function readStream() {
          if (!reader) return;

          reader.read()
            .then(({ done, value }) => {
              if (done) {
                if (timeoutHandle) clearTimeout(timeoutHandle);
                // Flush any remaining buffered content
                if (lineBuffer.trim()) {
                  processLine(lineBuffer);
                }
                addBreadcrumb('SSE stream completed', {
                  elapsedMs: Date.now() - startedAt,
                  dataEventCount,
                  bytesReceived
                });
                console.debug('[SSELink] Stream completed normally');
                observer.complete();
                return;
              }

              // Append decoded chunk to the leftover buffer
              bytesReceived += value?.byteLength ?? 0;
              lineBuffer += decoder.decode(value, { stream: true });
              const lines = lineBuffer.split('\n');
              lineBuffer = lines.pop() ?? '';

              for (const line of lines) {
                processLine(line);
              }

              readStream();
            })
            .catch(error => {
              if (timeoutHandle) clearTimeout(timeoutHandle);
              if (isClosed || controller.signal.aborted) return;
              const errorMsg = error?.message || 'Unknown error';
              const streamError = new Error(`SSE stream error: ${errorMsg}`);
              captureFailure(streamError, 'stream');
              console.debug('[SSELink] Stream read error:', errorMsg, getFailureContext('stream'));
              observer.error(streamError);
            });
        }

        readStream();
      }).catch(error => {
        if (timeoutHandle) clearTimeout(timeoutHandle);
        if (isClosed || controller.signal.aborted) return;

        const isBackgroundedLoadFailure = document.hidden &&
          error?.name === 'TypeError' &&
          error?.message?.toLowerCase() === 'load failed';
        if (isBackgroundedLoadFailure) {
          addBreadcrumb('SSE connection failed while page was hidden', {
            ...getFailureContext('connection'),
            errorName: error?.name,
            errorMessage: error?.message
          });
          observer.complete();
          return;
        }

        const errorMsg = error?.message || 'Unknown error';
        const connectionError = new Error(`SSE connection error: ${errorMsg}`);
        captureFailure(connectionError, 'connection');
        console.debug('[SSELink] Connection error:', errorMsg, getFailureContext('connection'));
        observer.error(connectionError);
      });

      // Return cleanup function
      return () => {
        isClosed = true;
        document.removeEventListener('visibilitychange', visibilityListener);
        addBreadcrumb('SSE request cleaned up', {
          elapsedMs: Date.now() - startedAt,
          hidden: document.hidden
        });
        if (timeoutHandle) clearTimeout(timeoutHandle);
        controller.abort();
        if (reader) reader.cancel().catch(() => {});
      };
    });
  }
}